import { Component, DestroyRef, computed, inject, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { DarkMode } from 'app/account-settings/models/user.model';
import { CurrentContextService } from 'app/core/current-context.service';
import { SnackBarService } from 'app/core/snack-bar.service';
import { replaceUserField } from 'app/framework/user-patch';
import { SettingsService } from 'app/shared/services/admin-settings.service';
import { ThemeService } from 'app/shared/services/theme.service';
import { UserService } from 'app/user/user.service';

@Component({
  selector: 'app-theme-toggle',
  templateUrl: './theme-toggle.component.html',
  styleUrls: ['./theme-toggle.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ThemeToggleComponent {
  private readonly themeService = inject(ThemeService);
  private readonly userService = inject(UserService);
  private readonly currentContextService = inject(CurrentContextService);
  private readonly settingsService = inject(SettingsService);
  private readonly snackbar = inject(SnackBarService);
  private readonly destroyRef = inject(DestroyRef);

  // Effective (resolved) theme — already accounts for DeviceSettingsBased via prefers-color-scheme.
  public readonly isDark = toSignal(this.themeService.isDarkThemeBS, { initialValue: false });
  public readonly tooltipKey = computed(() =>
    this.isDark() ? 'theme-toggle.switch-to-light' : 'theme-toggle.switch-to-dark',
  );

  public onToggleChange(): void {
    // Clicking always leaves "system" and selects an explicit choice (opposite of what's showing).
    const target = this.isDark() ? DarkMode.AlwaysLight : DarkMode.AlwaysDark;

    // Frontend-first: apply the new theme immediately for a snappy toggle. Nothing is persisted
    // yet, so if the save below fails the change simply disappears on the next page load.
    this.themeService.userTheme = target;
    const settings = this.settingsService.getCurrentSettings();
    const hadOverride = settings.adminTheme !== null;
    if (hadOverride) {
      // The topbar toggle is authoritative: drop any superadmin override (= "Reset"/null) so the
      // user's choice is visible. In memory only here; persisted once the save succeeds.
      this.settingsService.updateSettings({ ...settings, adminTheme: null }, false);
    }
    this.themeService.changeTheme(target);

    // Persist in the background.
    this.userService
      .patchCurrentUser([replaceUserField('darkMode', target)])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          if (hadOverride) {
            this.settingsService.updateSettings({ ...settings, adminTheme: null }, true);
          }
          const context = this.currentContextService.currentContext;
          if (context) {
            this.currentContextService.updateCurrentContextUser({ ...context.user, darkMode: target });
          }
        },
        error: () => this.snackbar.error('generic.error'),
      });
  }
}
